import { useAuth } from '../contexts/auth-context';
import { useFollows, useSessions } from '../hooks/useApi';
import { matchesSessionSearch } from '../utils/dateNavigation';
import { fmtDate } from '../utils/format';
import AsyncBoundary from './AsyncBoundary';
import EmptyState from './EmptyState';
import RankingRow from './RankingRow';
import AccountStatus from './AccountStatus';
import { Button } from './ui';

// Gevolgde schaatsers op de geselecteerde dag.
//
// Dit was een eigen tabblad (/volgend); het is nu het "Volgend"-filter binnen
// Baan. Daarom heeft het hier geen eigen paginatitel meer — Baan levert de kop.
export default function FollowingView({ onViewTransponder, date, query = '' }) {
  const { loggedIn, login } = useAuth();
  const { data: followsData, loading: followsLoading, error: followsError, refetch: refetchFollows } = useFollows(loggedIn);
  const { data: feedData, loading: feedLoading, error: feedError, refetch: refetchFeed } = useSessions(loggedIn ? date : null);

  if (!loggedIn) {
    return (
      <EmptyState
        title="Log in om schaatsers te volgen"
        sub="Volg vrienden en zie hun recente ritten op één plek."
        action={
          <Button variant="primary" className="mt-4" onClick={login}>
            Inloggen
          </Button>
        }
      />
    );
  }

  const following = followsData?.following ?? [];
  const ids = new Set(following.map(item => item.transponder));
  const items = (feedData?.sessions ?? []).filter(item =>
    ids.has(item.transponder) && matchesSessionSearch(item, query)).map(item => ({ ...item, date }));
  const loading = followsLoading || feedLoading;

  return (
    <div className="flex flex-col gap-3">
      {(followsError || feedError) && <div><AccountStatus error={`Gevolgde schaatsers konden niet worden geladen (${followsError || feedError}).`} /><Button variant="outline" className="mt-3" onClick={() => { refetchFollows(); refetchFeed(); }}>Opnieuw proberen</Button></div>}
      {!followsError && !feedError && following.length > 0 && (
        <p className="text-[14.5px] text-ink-3">
          Je volgt {following.length} {following.length === 1 ? 'schaatser' : 'schaatsers'}.
        </p>
      )}

      <AsyncBoundary
        loading={loading}
        loadingText="Gevolgde ritten laden…"
        empty={
          followsError || feedError ? <></> : following.length === 0 ? (
            <EmptyState
              title="Je volgt nog niemand"
              sub="Open het sessieoverzicht van een schaatser en tik op “+ Volgen”."
            />
          ) : items.length === 0 ? (
            <EmptyState
              title="Geen gevolgde ritten op deze dag"
              sub="Kies een andere datum of pas je zoekterm aan."
            />
          ) : null
        }
      >
        {items.length > 0 && (
          <div className="flex flex-col gap-1.5" role="list">
            {items.map(it => (
              <RankingRow
                key={`${it.transponder}-${it.date}`}
                onClick={() => onViewTransponder(it.transponder, it.date)}
                ariaLabel={`${it.nickname || it.transponder} op ${fmtDate(it.date)} — bekijk sessie`}
                name={it.nickname || it.transponder}
                sub={<span className="text-[13.5px] text-ink-3">{fmtDate(it.date)}</span>}
                stat={
                  <>
                    <span className="font-mono text-[21px] font-extrabold leading-none text-accent-600">
                      {it.finishCrossings}
                    </span>
                    <span className="font-condensed text-[13px] font-bold uppercase tracking-[0.08em] text-ink-3">
                      doorkomsten
                    </span>
                  </>
                }
              />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}
